/**
 * Chargement à la demande des pages (React.lazy) — version robuste.
 *
 * Pourquoi : les pages d'administration ne sont plus dans le fichier de code
 * principal (un donateur qui ouvre /c/:id depuis WhatsApp ne les télécharge
 * plus). Elles arrivent en « morceaux » séparés, au moment où on les ouvre.
 * Un morceau peut ne pas arriver : 4G qui décroche, ou nouvelle version du
 * site mise en ligne pendant que l'onglet était ouvert (ancien morceau absent
 * → 404). Sans précaution, React affiche alors un écran blanc.
 *
 * Ce que fait `lazyAvecRecharge` quand un morceau ne se charge pas :
 *   1. un 2e essai après une courte pause (le réseau revient souvent vite) ;
 *   2. sinon, UN rechargement automatique de la page (récupère la version en
 *      ligne), protégé contre les boucles : horodatage en sessionStorage, pas
 *      de 2e rechargement automatique avant DELAI_ANTI_BOUCLE_MS ;
 *   3. sinon (hors ligne, stockage bloqué, déjà rechargé) : l'erreur remonte à
 *      la limite d'erreur (LimiteErreurChargement), qui propose « Recharger ».
 */
import { lazy } from "react";

const CLE_RECHARGE = "trm_recharge_code";
const DELAI_ANTI_BOUCLE_MS = 30000;
const PAUSE_AVANT_2E_ESSAI_MS = 1500;

/** Vrai si l'erreur vient d'un morceau de code qui n'a pas pu être chargé. */
export function estErreurDeChargement(erreur) {
  if (!erreur) return false;
  if (erreur.name === "ChunkLoadError") return true;
  if (erreur.code === "CSS_CHUNK_LOAD_FAILED") return true;
  return /Loading (CSS )?chunk [\w-]+ failed/i.test(String(erreur.message || ""));
}

function estHorsLigne() {
  try {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  } catch {
    return false;
  }
}

/**
 * Recharge la page une seule fois. Renvoie false (sans recharger) si un
 * rechargement automatique vient déjà d'avoir lieu, si l'appareil est hors
 * ligne (le navigateur afficherait sa propre page d'erreur, pire que notre
 * bouton) ou si le stockage de session est inaccessible (navigation privée
 * stricte : sans mémoire, on ne prend aucun risque de boucle).
 * `recharger` n'est remplacé que par les tests.
 */
export function rechargerUneFois(recharger = () => window.location.reload()) {
  if (estHorsLigne()) return false;
  try {
    const dernier = Number(window.sessionStorage.getItem(CLE_RECHARGE)) || 0;
    if (Date.now() - dernier < DELAI_ANTI_BOUCLE_MS) return false;
    window.sessionStorage.setItem(CLE_RECHARGE, String(Date.now()));
  } catch {
    return false;
  }
  recharger();
  return true;
}

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** React.lazy + 2e essai + rechargement unique protégé contre les boucles. */
export function lazyAvecRecharge(importer) {
  return lazy(() =>
    importer().catch((erreur) => {
      if (!estErreurDeChargement(erreur)) throw erreur;
      return pause(PAUSE_AVANT_2E_ESSAI_MS)
        .then(importer)
        .catch((erreur2) => {
          if (estErreurDeChargement(erreur2) && rechargerUneFois()) {
            // La page se recharge : on laisse le repli de chargement affiché.
            return new Promise(() => {});
          }
          throw erreur2;
        });
    }),
  );
}
